"use client";

import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
export default function TheHabitHero() {
  const {
    ref: textRef,
    isVisible: isTextVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: imgRef,
    isVisible: isImgVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="236928" id="precision-in-every-word" className="relative min-h-screen flex items-center overflow-hidden bg-background text-foreground">
      {}
      <div className="absolute inset-0 z-0 opacity-[0.03] pointer-events-none" style={{
      backgroundImage: 'linear-gradient(var(--color-foreground) 1px, transparent 1px), linear-gradient(90deg, var(--color-foreground) 1px, transparent 1px)',
      backgroundSize: '64px 64px'
    }} />
      
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,_var(--color-primary)_0%,_transparent_60%)] opacity-[0.07] pointer-events-none" />

      <div className="container mx-auto px-6 md:px-8 relative z-10 py-20 lg:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          
          {}
          <div ref={textRef} className={`lg:col-span-7 flex flex-col items-start gap-6 transition-all duration-700 ease-out ${isTextVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"}`}>
            

            <h1 className="font-sans font-extrabold text-5xl md:text-7xl lg:text-8xl leading-[0.95] text-foreground tracking-tighter">
              Precision is our promise. <br />
              <span className="text-primary">Empathy is our bridge.</span>
            </h1>
            
            <div className="max-w-xl space-y-6">
              <p className="font-sans text-lg md:text-xl text-muted-foreground leading-relaxed">
                Founded by a certified translator with 3 years of specialized experience in medical and legal settings. We don't just translate—we bridge understanding in critical moments where precision determines outcomes.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Button asChild size="lg" className="h-14 px-10 text-base font-bold rounded-sm bg-primary text-primary-foreground shadow-md hover:shadow-lg hover:translate-y-[-2px] transition-all duration-200">
                  <Link to="/ConnectPage">Get a Quote</Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="h-14 px-10 text-base font-bold rounded-sm border-border hover:bg-accent transition-all duration-200">
                  
                </Button>
              </div>

              
            </div>
          </div>

          {}
          <div ref={imgRef} className={`lg:col-span-5 relative transition-all duration-1000 delay-200 ease-out ${isImgVisible ? "opacity-100 scale-100" : "opacity-0 scale-95"}`}>
            <div className="relative aspect-[4/5] w-full bg-muted rounded-sm overflow-hidden border border-border shadow-2xl">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1649121/img/female_linguist_translator_portrait.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1649121/img/female_linguist_translator_portrait.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1649121/img/female_linguist_translator_portrait.png-wvc-sizes" alt="Professional female linguist and translator portrait" className="w-full h-full object-cover grayscale-[0.3] hover:grayscale-0 transition-all duration-700" loading="lazy" />
              
              {}
              <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-background/90 to-transparent backdrop-blur-sm">
                <div className="flex justify-between items-end">
                  <div className="space-y-1">
                    <p className="font-mono text-[10px] text-primary font-bold tracking-widest uppercase">Secure Node 01</p>
                    <p className="font-sans text-xs text-foreground/80">Certified Professional Environment</p>
                  </div>
                  <div className="flex gap-1">
                    {[1, 2, 3, 4].map(i => <div key={i} data-index={i} className="w-1 h-4 bg-primary/20 rounded-full overflow-hidden">
                        <div className="w-full h-full bg-primary animate-pulse" style={{
                      animationDelay: `${i * 0.2}s`
                    }} />
                      </div>)}
                  </div>
                </div>
              </div>
            </div>

            {}
            <div className="absolute -top-4 -right-4 w-24 h-24 border-t-2 border-r-2 border-primary/30 pointer-events-none" />
            <div className="absolute -bottom-4 -left-4 w-24 h-24 border-b-2 border-l-2 border-primary/30 pointer-events-none" />
          </div>

        </div>
      </div>
    </section>;
}